import * as React from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useMe } from "../hooks/useMe.js";
import { useSubscribe } from "../hooks/useSubscribe.js";
import { apiFetch, ApiError } from "../lib/api.js";
import { normalizePhone, formatPhone, formatPhoneInput } from "../lib/phone.js";
import { isValidName, NAME_HELP } from "../lib/validation.js";
import { OtpGrid } from "./OtpGrid.js";
import { SupportHint } from "./MailtoContact.js";
import { ConsentDisclosure, MarketingConsent } from "./MarketingConsent.js";
import { MultiSelectCombobox, type MultiSelectOption } from "./MultiSelectCombobox.js";
// Import from configs.js (not the testLanding index): the index also exports
// the route/template, and the template's useStartDiagnostic hook reads this
// component — importing the index here would close that cycle and leave
// TEST_LANDING_CONFIGS undefined at module-eval time.
import { TEST_LANDING_BY_SLUG, TEST_LANDING_CONFIGS } from "../routes/testLanding/configs.js";
import { US_STATES } from "../lib/us-states.js";
import { track } from "../lib/analytics.js";
import { gameForPhase } from "../lib/diagnostic-games.js";
import {
  peekDiagnosticTestSlug,
  readAndClearDiagnosticTestSlug,
} from "../lib/diagnostic-test-slug.js";
import { leadMagnetForSlug, leadMagnetHref, triggerDownload } from "../lib/lead-magnets.js";
import { useToastOptional } from "./ToastProvider.js";
import { trackMetaEvent } from "../lib/meta-pixel.js";
import { buildPhoneVerifyBody, getAttribution, leadEventParams } from "../lib/attribution.js";
import { getExposure } from "../lib/tl-experiment.js";
import "../styles/auth-gate.css";

/**
 * Field Day access-gate modals (auth + paywall).
 *
 * Two layers of click-time gating sit above the router:
 *
 *  - **Auth gate** (Layer 1) — three variants of the same chrome that
 *    appear when a signed-out visitor clicks a link to a gated route:
 *    `dashboard`, `placement-test`, `game-play`. Differ only in the
 *    eyebrow / H2 / body copy and the highlighted word.
 *  - **Paywall** (Layer 2) — visually distinct (dark→cream split, price
 *    chip) modal that appears when a signed-in free-tier visitor tries
 *    to start a game-play surface.
 *
 * Two entry points:
 *
 *  - **Document click listener.** Plain `<a href="/dashboard">` or
 *    `<a href="/placement">` clicks are caught in capture phase. Power-
 *    user modifier clicks bypass. Direct URL access still hits the
 *    route-level redirect — this is a UX layer, not a router guard.
 *  - **Imperative API.** `useAuthGate().openAuthGate('game-play')` and
 *    `openPaywall()` for surfaces where the action is a `<button>` (the
 *    Play CTA on `/games/:slug` calls `POST /v1/plays` before
 *    navigating, so there's no anchor to intercept).
 *
 * Reference design: design-files / auth-gate-handoff.md + auth-gate.js
 * (vanilla JS prototype). Visual tokens live in styles/auth-gate.css.
 */

// ─── Public types ────────────────────────────────────────────────────

/** Auth-gate variant key. Drives copy + accessibility id. */
export type AuthSource =
  | "dashboard"
  | "placement-test"
  | "game-play"
  | "quiz-studio"
  | "quiz-continue"
  | "blog-comment";

type GateKind = "auth" | "paywall";

type DismissReason = "esc" | "x" | "overlay" | "maybe_later" | "cta" | "route";

interface AuthGateContextValue {
  /** Open the auth-gate modal for the given variant. Pass `intendedHref`
   * when the user clicked into a specific URL we want to remember (for
   * post-sign-in redirect, when that hookup lands). */
  openAuthGate: (source: AuthSource, intendedHref?: string) => void;
  /** Open the Premium paywall modal. */
  openPaywall: (intendedHref?: string) => void;
  close: (reason: DismissReason) => void;
  isOpen: boolean;
}

// ─── Auth-gate copy registry ────────────────────────────────────────

interface AuthVariant {
  source: AuthSource;
  /** Pathname predicate for the document click listener. */
  matchPath: (pathname: string) => boolean;
  eyebrow: string;
  /** H2 is rendered as `{titleBefore}<highlight>{titleHighlight}</highlight>{titleAfter}` */
  titleBefore: string;
  titleHighlight: string;
  titleAfter: string;
  /** Body paragraph. `**bold**` segments wrapped in <strong>. */
  body: React.ReactNode;
}

const AUTH_VARIANTS: AuthVariant[] = [
  {
    source: "dashboard",
    matchPath: (p) => p === "/dashboard" || p.startsWith("/dashboard/"),
    eyebrow: "Not signed in yet",
    titleBefore: "Create a free account for your ",
    titleHighlight: "dashboard",
    titleAfter: ".",
    body: (
      <>
        Your <strong>placements, recent plays, and progress</strong> live behind your account. Sign
        in if you already have one — or create a free account to take the placement and unlock your
        personalized dashboard.
      </>
    ),
  },
  {
    source: "placement-test",
    // The placement gate fires on the ACTION (clicking the Start button
    // on /diagnostic), not on NAVIGATION (clicking a landing-page link
    // that points at /diagnostic). The intro page itself is informational
    // and should be reachable by anyone — only kicking off the test
    // requires sign-in. So the document click listener never matches
    // here; DiagnosticRoute's handleStart calls openAuthGate(
    // 'placement-test') imperatively when the visitor hits Start while
    // signed out.
    matchPath: () => false,
    eyebrow: "Almost ready",
    titleBefore: "Create a free account to start your ",
    titleHighlight: "placement",
    titleAfter: ".",
    body: (
      <>
        The placement is <strong>free and takes about 12 minutes</strong>. We save your results to
        your account so the games can adapt to where you actually are — and you can pick up where
        you left off next time.
      </>
    ),
  },
  {
    source: "game-play",
    // Game-play surfaces are entered via a button, not a plain anchor — so
    // the document listener doesn't catch them. Still listed here for the
    // imperative path to look up copy.
    matchPath: () => false,
    eyebrow: "Almost there",
    titleBefore: "Create a free account to ",
    titleHighlight: "play",
    titleAfter: ".",
    body: (
      <>
        Your <strong>scores, streaks, and unlocked levels</strong> live with your account, so we can
        pick up exactly where you left off. New here? Create a free account to take the placement
        and start playing.
      </>
    ),
  },
  {
    source: "quiz-studio",
    // Quiz Studio is now anonymous-friendly (TEX-2398): the first quiz is free, no
    // account. So the document click-listener no longer blocks /quizzes[/new] —
    // matchPath stays false. The variant is kept for the IMPERATIVE path only:
    // the composer opens it when an anonymous visitor tries to make a SECOND quiz
    // (their one free quiz is used → "create an account to make more").
    matchPath: () => false,
    eyebrow: "Loving it?",
    titleBefore: "Create a free account to make ",
    titleHighlight: "more quizzes",
    titleAfter: ".",
    body: (
      <>
        Your first quiz is on us. Create a free account to make <strong>more</strong> — we&rsquo;ll
        save them so you can replay, share, and pick up on any device.
      </>
    ),
  },
  {
    source: "quiz-continue",
    // The 5-correct play gate (TEX-2398): fired imperatively by PlayShell when an
    // anonymous player has answered 5 correct in their own quiz. Button-driven, so
    // the document listener never matches.
    matchPath: () => false,
    eyebrow: "Nice work",
    titleBefore: "Create a free account to ",
    titleHighlight: "keep playing",
    titleAfter: ".",
    body: (
      <>
        You&rsquo;re on a roll! Create a free account to <strong>finish this quiz</strong> — and so
        we can save your scores, streaks, and quizzes to pick up anywhere.
      </>
    ),
  },
  {
    source: "blog-comment",
    // Fired imperatively by BlogPostRoute's comment form gate — a signed-out
    // visitor never sees /blog[/:slug] behind the document click-listener,
    // only the comment box itself is gated.
    matchPath: () => false,
    eyebrow: "Join the huddle",
    titleBefore: "Create a free account to ",
    titleHighlight: "comment",
    titleAfter: ".",
    body: (
      <>
        Comments are tied to your account so <strong>your name</strong> shows on what you post — no
        anonymous drive-bys. Takes ten seconds.
      </>
    ),
  },
];

function findVariantBySource(source: AuthSource): AuthVariant {
  const v = AUTH_VARIANTS.find((x) => x.source === source);
  if (!v) throw new Error(`No AuthVariant for source "${source}"`);
  return v;
}

/** Returns the variant whose `matchPath` predicate matches `pathname`,
 * or null if no listener-driven gate applies. Honors absolute URLs on
 * the same origin too. */
function matchVariantByHref(href: string | null, origin: string): AuthVariant | null {
  if (!href) return null;
  if (href === "#") return null;

  let pathname: string;
  if (/^https?:\/\//i.test(href)) {
    try {
      const u = new URL(href);
      if (u.origin !== origin) return null;
      pathname = u.pathname;
    } catch {
      return null;
    }
  } else if (href.startsWith("/")) {
    // Strip query and hash for matching.
    pathname = href.split(/[?#]/)[0] ?? href;
  } else {
    return null;
  }
  return AUTH_VARIANTS.find((v) => v.matchPath(pathname)) ?? null;
}

// ─── Standardized-test options ───────────────────────────────────────

/**
 * "I'm studying for…" combobox options, derived once from the portal's
 * canonical test-landing list. `secondary` shows the state for per-state tests
 * (national tests stay clean) so duplicate short names disambiguate.
 */
const STUDYING_FOR_OPTIONS: MultiSelectOption[] = TEST_LANDING_CONFIGS.map((t) => ({
  value: t.slug,
  label: t.shortName,
  secondary: t.scope === "national" ? undefined : t.scope,
}));

// ─── Context ─────────────────────────────────────────────────────────

const AuthGateContext = React.createContext<AuthGateContextValue | null>(null);

export function useAuthGate(): AuthGateContextValue {
  const ctx = React.useContext(AuthGateContext);
  if (!ctx) {
    throw new Error("useAuthGate must be used inside <AuthGateProvider>");
  }
  return ctx;
}

/**
 * Non-throwing variant — returns null when rendered outside <AuthGateProvider>.
 * The provider is app-wide in production (App.tsx), so this is null only in
 * isolated component/route tests. Lets always-mounted nav chrome (the mobile
 * drawer + tab bar, via useStartDiagnostic) render in those tests without
 * crashing the tree, while real usage still opens the gate normally.
 */
export function useAuthGateOptional(): AuthGateContextValue | null {
  return React.useContext(AuthGateContext);
}

// ─── Provider ────────────────────────────────────────────────────────

interface GateState {
  kind: GateKind;
  source: AuthSource | null; // null for paywall
  intendedHref: string | null;
}

export function AuthGateProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { data: me } = useMe();
  const signedIn = !!me?.user;
  const [state, setState] = React.useState<GateState | null>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const location = useLocation();
  const isOpen = state !== null;

  const close = React.useCallback((_reason: DismissReason) => {
    setState(null);
    const last = triggerRef.current;
    triggerRef.current = null;
    if (last && typeof last.focus === "function") {
      try {
        last.focus();
      } catch {
        /* no-op */
      }
    }
  }, []);

  const openAuthGate = React.useCallback((source: AuthSource, intendedHref?: string) => {
    track.upsellViewed({ kind: "auth", source });
    setState({ kind: "auth", source, intendedHref: intendedHref ?? null });
  }, []);

  const openPaywall = React.useCallback((intendedHref?: string) => {
    track.upsellViewed({ kind: "paywall", source: "premium" });
    setState({ kind: "paywall", source: null, intendedHref: intendedHref ?? null });
  }, []);

  // Auto-close on route change (e.g. a completed in-modal auth navigates to
  // the intended destination). The functional update reads nothing from the
  // render closure (the state setter is stable), so the dep array is
  // genuinely exhaustive — no stale-dependency suppression needed, and it
  // stays correct whether or not react-hooks/exhaustive-deps is enforced.
  React.useEffect(() => {
    setState((prev) => (prev === null ? prev : null));
  }, [location.pathname]);

  // Document-level click interceptor — catches plain <a> clicks to
  // /dashboard and /placement. The game-play surface is button-driven, so
  // its consumers call openAuthGate() / openPaywall() directly.
  React.useEffect(() => {
    function onClick(e: MouseEvent) {
      if (signedIn) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0) return;

      const target = e.target;
      if (!(target instanceof Element)) return;
      const a = target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href");
      const variant = matchVariantByHref(href, window.location.origin);
      if (!variant) return;

      e.preventDefault();
      e.stopPropagation();
      triggerRef.current = a as HTMLElement;
      openAuthGate(variant.source, href ?? undefined);
    }
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
    };
  }, [signedIn, openAuthGate]);

  const ctx = React.useMemo<AuthGateContextValue>(
    () => ({ openAuthGate, openPaywall, close, isOpen }),
    [openAuthGate, openPaywall, close, isOpen],
  );

  return (
    <AuthGateContext.Provider value={ctx}>
      {children}
      {state &&
        (state.kind === "auth" ? (
          <AuthVariantModal
            variant={findVariantBySource(state.source!)}
            intendedHref={state.intendedHref}
            onClose={close}
          />
        ) : (
          <PaywallModal onClose={close} intendedHref={state.intendedHref} />
        ))}
    </AuthGateContext.Provider>
  );
}

// ─── Shared modal hooks ──────────────────────────────────────────────

/** Body scroll lock + Esc handler + focus-primary-CTA. Used by both the
 * auth-variant modal and the paywall modal so they stay in sync. */
function useModalChrome(onClose: (reason: DismissReason) => void): {
  primaryRef: React.RefObject<HTMLButtonElement>;
} {
  const primaryRef = React.useRef<HTMLButtonElement>(null) as React.RefObject<HTMLButtonElement>;

  React.useLayoutEffect(() => {
    document.body.classList.add("auth-gate-open");
    return () => {
      document.body.classList.remove("auth-gate-open");
    };
  }, []);

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      primaryRef.current?.focus();
    }, 0);
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose("esc");
    }
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return { primaryRef };
}

// ─── Auth-variant modal ──────────────────────────────────────────────

interface AuthVariantModalProps {
  variant: AuthVariant;
  intendedHref: string | null;
  onClose: (reason: DismissReason) => void;
}

/** Where to continue after a successful in-modal auth when no explicit
 * intendedHref was captured (e.g. the imperative openAuthGate without a
 * URL). The gate usually passes intendedHref, so these are fallbacks. */
const DEFAULT_DEST: Record<AuthSource, string> = {
  dashboard: "/dashboard",
  "placement-test": "/diagnostic",
  "game-play": "/games",
  "quiz-studio": "/quizzes",
  // Fallback only — PlayShell always passes the current play URL as intendedHref
  // so the post-signup navigate is a no-op and the paused game resumes in place.
  "quiz-continue": "/quizzes",
  // Fallback only — the comment gate always passes the current post's path as
  // intendedHref, so the post-signup navigate is a no-op and the visitor lands
  // back on the same post with the (now-unlocked) comment form.
  "blog-comment": "/blog",
};

/** Which inner screen of the gate is showing. Most gates open on "signup" (the
 * create-account form); the quiz funnels (TEX-2398) open on "intro" first — a
 * themed explainer with a "Back to quizzes" escape — so a mid-play / second-quiz
 * signup ask isn't thrown at the visitor with no context. The form's "sign in
 * instead" / "create an account" toggles morph between signin/signup in place. */
type AuthView = "intro" | "signin" | "signup";

/**
 * The auth gate, as a self-contained multi-view modal. It opens on the
 * create-account form and swaps to phone-OTP sign-in (and back) WITHOUT
 * navigating; on success it refreshes `/me` and continues to the intended
 * destination. The standalone /signin + /signup pages are unaffected — this
 * is a parallel, higher-conversion path.
 */
function AuthVariantModal({
  variant,
  intendedHref,
  onClose,
}: AuthVariantModalProps): React.ReactElement | null {
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Quiz funnels (TEX-2398) open on a themed "intro" explainer first; every other
  // gate opens straight on the sign-up form (the old chooser interstitial was
  // removed for conversion). The form carries its own "sign in instead" toggle.
  const introFirst = variant.source === "quiz-continue" || variant.source === "quiz-studio";
  const [view, setView] = React.useState<AuthView>(introFirst ? "intro" : "signup");

  // "Back to quizzes" — leave the gate and return to the quizzes page. Used by
  // the intro's back button, and as the dismiss for the in-play gate (see below).
  // The in-play (quiz-continue) gate only ever shows to GUESTS, who have no
  // "My Quizzes" — so send them to the community browse to keep playing without
  // an account (TEX-2706 #2), rather than the empty owner dashboard. Other gates
  // (quiz-studio composer) keep returning to /quizzes.
  const goBackToQuizzes = React.useCallback(() => {
    onClose("maybe_later");
    navigate(variant.source === "quiz-continue" ? "/community" : "/quizzes", { replace: true });
  }, [onClose, navigate, variant.source]);

  // The in-play gate (quiz-continue) pauses a running game behind the modal, so
  // ANY dismiss must leave to /quizzes — otherwise closing the modal would strand
  // a paused game with no UI. Every other gate just closes in place.
  const onDismiss = React.useCallback(
    (reason: DismissReason) => {
      if (variant.source === "quiz-continue") goBackToQuizzes();
      else onClose(reason);
    },
    [variant.source, goBackToQuizzes, onClose],
  );

  // Body scroll lock for the modal's lifetime.
  React.useLayoutEffect(() => {
    document.body.classList.add("auth-gate-open");
    return () => {
      document.body.classList.remove("auth-gate-open");
    };
  }, []);

  // Esc dismisses from any view.
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onDismiss("esc");
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  function onOverlayMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) onDismiss("overlay");
  }

  // Shared success handler for both flows: refresh the session, dismiss the
  // modal, then continue to wherever the visitor was originally headed.
  const completeAuth = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["me"] });

    // Placement gates: the visitor already pressed a "Start placement" CTA —
    // finishing auth IS the consent to begin. Start phase 1 right here and
    // land straight in the game, instead of bouncing through the /diagnostic
    // intro page for a second Start click (TEX-1637 funnel). Mirrors
    // useStartDiagnostic's signed-in branch exactly (same endpoint/body,
    // same router state contract — /diagnostic/play skips its own /start
    // when `state.start` is present). On failure, fall through to the intro
    // page, which still has its own Start button and error surface.
    if (variant.source === "placement-test") {
      try {
        // Tailor the run to the test the visitor came from. The slug rides
        // sessionStorage from useStartDiagnostic's signed-out branch (the
        // only opener of placement-test gates, so the stash is fresh-or-
        // cleared by construction); if storage was unavailable, fall back to
        // deriving it from the test-landing path under the modal.
        const stashed = readAndClearDiagnosticTestSlug();
        const pathSlug = window.location.pathname.split("/").filter(Boolean);
        const derived =
          pathSlug.length === 1 && pathSlug[0] && TEST_LANDING_BY_SLUG[pathSlug[0]]
            ? pathSlug[0]
            : null;
        const testSlug = stashed ?? derived;
        const start = await apiFetch<{ diagnostic_run_id: string }>("/v1/diagnostic/start", {
          method: "POST",
          body: {
            game_slug: gameForPhase(1),
            ...(testSlug ? { test_slug: testSlug } : {}),
          },
        });
        track.placementStarted({ diagnostic_run_id: start.diagnostic_run_id });
        onClose("route");
        navigate("/diagnostic/play?returnTo=%2Fplacement", { replace: true, state: { start } });
        return;
      } catch {
        // fall through to the intro page
      }
    }

    const dest = intendedHref ?? DEFAULT_DEST[variant.source];
    // Close explicitly: when dest equals the current path (e.g. the
    // placement gate fires on /diagnostic), navigate() is a no-op and the
    // provider's route-change auto-close wouldn't fire.
    onClose("route");
    navigate(dest, { replace: true });
  }, [queryClient, intendedHref, variant.source, onClose, navigate]);

  // "Challenge your kid" (TEX-1765): the challenge step replaces the auto-start.
  // Hand the parent to the lobby (participate) / sent view (sit-out) at
  // /challenge/<code>, which is role-aware.
  const startChallenge = React.useCallback(
    (code: string) => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      onClose("route");
      navigate(`/challenge/${code}`, { replace: true });
    },
    [queryClient, onClose, navigate],
  );

  const titleId = "auth-gate-title";
  const node = (
    <div
      ref={overlayRef}
      className="auth-gate__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-source={variant.source}
      data-view={view}
      onMouseDown={onOverlayMouseDown}
    >
      <div className="auth-gate__modal">
        <CloseButton onClick={() => onDismiss("x")} />
        {view === "intro" && (
          <div className="auth-gate__step">
            <span className="auth-gate__sticker">Free</span>
            <div className="auth-gate__head">
              <span className="auth-gate__eye">{variant.eyebrow}</span>
              <h2 id={titleId} className="auth-gate__title">
                {variant.titleBefore}
                <span className="auth-gate__highlight">{variant.titleHighlight}</span>
                {variant.titleAfter}
              </h2>
              <p className="auth-gate__body">{variant.body}</p>
            </div>
            <form
              className="auth-gate__form auth-gate__form--final"
              onSubmit={(e) => {
                e.preventDefault();
                setView("signup");
              }}
              noValidate
            >
              <button type="submit" className="auth-gate__submit">
                Create a free account
                <span className="auth-gate__arc" aria-hidden="true">
                  <ArrowIcon />
                </span>
              </button>
              <button
                type="button"
                className="auth-gate__cta auth-gate__cta--secondary"
                onClick={goBackToQuizzes}
              >
                &larr; Back to quizzes
              </button>
            </form>
          </div>
        )}
        {view === "signin" && (
          <SignInView
            titleId={titleId}
            onExit={() => setView("signup")}
            onSwitchToSignUp={() => setView("signup")}
            onComplete={completeAuth}
          />
        )}
        {view === "signup" && (
          <SignUpView
            titleId={titleId}
            onSwitchToSignIn={() => setView("signin")}
            onComplete={completeAuth}
            offerChallenge={variant.source === "placement-test"}
            onStartChallenge={startChallenge}
          />
        )}
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}

// ─── View: phone-OTP sign-in (phone → code) ──────────────────────────

interface SignInViewProps {
  titleId: string;
  onExit: () => void;
  onSwitchToSignUp: () => void;
  onComplete: () => Promise<void>;
}

function SignInView({
  titleId,
  onExit,
  onSwitchToSignUp,
  onComplete,
}: SignInViewProps): React.ReactElement {
  const [step, setStep] = React.useState<"phone" | "otp">("phone");
  const [phone, setPhone] = React.useState("");
  const [normalizedPhone, setNormalizedPhone] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const phoneRef = React.useRef<HTMLInputElement>(null);
  const otp = useOtpVerification(normalizedPhone, onComplete);

  React.useEffect(() => {
    if (step !== "phone") return;
    const t = window.setTimeout(() => phoneRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [step]);

  const valid = normalizePhone(phone) !== null;

  async function sendCode(e?: React.FormEvent) {
    e?.preventDefault();
    if (busy) return;
    const normalized = normalizePhone(phone);
    if (!normalized) {
      setErr("Please enter a valid mobile number.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiFetch("/auth/phone-otp", {
        method: "POST",
        body: { phone: normalized },
      });
      setNormalizedPhone(normalized);
      otp.armForCode();
      setStep("otp");
    } catch (e2) {
      setErr(authErrorMessage(e2, "send"));
    } finally {
      setBusy(false);
    }
  }

  if (step === "otp") {
    return (
      <OtpStep
        titleId={titleId}
        eyebrow="Code sent"
        cta="Verify & sign in"
        phoneDisplay={normalizedPhone ? formatPhone(normalizedPhone) : phone}
        ctx={otp}
        onBack={() => {
          setStep("phone");
          setErr(null);
          otp.reset();
        }}
      />
    );
  }

  return (
    <>
      <span className="auth-gate__sticker">Sign in</span>
      <div className="auth-gate__head">
        <BackButton onClick={onExit} />
        <span className="auth-gate__eye">Welcome back</span>
        <h2 id={titleId} className="auth-gate__title">
          Sign in with your <span className="auth-gate__highlight">phone</span>.
        </h2>
        <p className="auth-gate__body">
          Pop in your mobile number and we&rsquo;ll text you a <strong>one-time code</strong>. No
          passwords to remember.
        </p>
      </div>
      <form className="auth-gate__form" onSubmit={sendCode} noValidate>
        <div className="auth-gate__field">
          <label className="auth-gate__label" htmlFor="auth-gate-phone">
            Mobile number <span className="auth-gate__req">*</span>
          </label>
          <input
            ref={phoneRef}
            id="auth-gate-phone"
            className="auth-gate__input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="(555) 123-4567"
            value={phone}
            onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
          />
        </div>
        {err && (
          <div className="auth-gate__err" role="alert">
            {err}
          </div>
        )}
        <button type="submit" className="auth-gate__submit" disabled={!valid || busy}>
          {busy ? "Sending…" : "Text me a code"}
          <span className="auth-gate__arc" aria-hidden="true">
            <ArrowIcon />
          </span>
        </button>
      </form>
      <div className="auth-gate__foot">
        <span>
          New here?{" "}
          <button type="button" className="auth-gate__link" onClick={onSwitchToSignUp}>
            Create a free account
          </button>
        </span>
      </div>
    </>
  );
}

// ─── View: create a free account (form → code) ───────────────────────

interface SignUpViewProps {
  titleId: string;
  onSwitchToSignIn: () => void;
  onComplete: () => Promise<void>;
  /** Show the "Challenge your kid" step for parents (placement-test gate only). */
  offerChallenge?: boolean;
  /** Finisher for the challenge step: close the gate + go to the lobby. */
  onStartChallenge?: (code: string) => void;
}

/**
 * Phone-first signup steps:
 *   1. "phone"   — mobile number + optional marketing opt-in → sends an OTP.
 *   2. "otp"     — verify the code; /auth/phone-verify creates + signs in the
 *                  user from the number alone (no profile required yet).
 *   3. "worry"   — only when signing up ON a test landing page (TEX-1639):
 *                  the one required open-text question ("What worries you
 *                  most about the {test}?") → /auth/test-worry. Skipped
 *                  everywhere else (main page, dashboard, game gates).
 *   4. "about"   — role + studying-for.
 *   5. "profile" — name (+ optional email) → /auth/complete-profile, then done.
 *
 * Account creation is therefore unconditional on the phone number — the
 * marketing checkbox is genuinely optional (TCPA: marketing consent can't gate
 * the service). The OTP send is transactional, so no opt-in is needed for it.
 * Each step's body remounts under key={step}, animating the transition.
 */
function SignUpView({
  titleId,
  onSwitchToSignIn,
  onComplete,
  offerChallenge = false,
  onStartChallenge,
}: SignUpViewProps): React.ReactElement {
  const location = useLocation();
  const [step, setStep] = React.useState<
    "phone" | "otp" | "worry" | "about" | "profile" | "challenge"
  >("phone");
  const [phone, setPhone] = React.useState("");
  const [normalizedPhone, setNormalizedPhone] = React.useState<string | null>(null);
  const [marketing, setMarketing] = React.useState(false);
  const [role, setRole] = React.useState<"parent" | "student">("parent");
  const [first, setFirst] = React.useState("");
  const [last, setLast] = React.useState("");
  const [childName, setChildName] = React.useState("");
  const [studyingFor, setStudyingFor] = React.useState<string[]>([]);
  // Home US state (2-letter abbr) — powers the lobby's "people near me" skew
  // (TEX-2256). Optional; "" means "not provided".
  const [homeState, setHomeState] = React.useState<string>("");
  const [worry, setWorry] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  // One-time opt-in nudge: shown when a parent tries to advance without ticking
  // the (optional) marketing box. They can still proceed without consent.
  const [showNudge, setShowNudge] = React.useState(false);
  const [nudgeResolved, setNudgeResolved] = React.useState(false);
  const phoneRef = React.useRef<HTMLInputElement>(null);
  const firstRef = React.useRef<HTMLInputElement>(null);
  const worryRef = React.useRef<HTMLTextAreaElement>(null);
  const leadFiredRef = React.useRef(false);
  const playbookFiredRef = React.useRef(false);
  const toast = useToastOptional();

  // The worry step exists only when this signup happens ON a test landing
  // page — the gate modal overlays the page and auto-closes on navigation,
  // so the pathname can't change mid-flow. The page's slug names the test
  // in the question. Deliberately NOT a stored "has ever seen a test page"
  // signal: signing up from the main page (or anywhere else) must not ask,
  // however many test pages this browser visited before. The experiment arm
  // (TEX-1637 exposure stamp, first-visit-wins) rides along for analytics
  // only. All hero arms (and control) share this identical step, so the
  // experiment stays unconfounded. Resolved once on mount.
  const [worryTest] = React.useState(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const slug = segments.length === 1 ? segments[0]! : undefined;
    const config = slug ? TEST_LANDING_BY_SLUG[slug] : undefined;
    return slug && config
      ? { slug, shortName: config.shortName, variant: getExposure()?.variant }
      : null;
  });

  // Lead magnet (a free downloadable guide) tied to the funnel this signup
  // started from. Prefer the slug under the modal (the test-landing pathname);
  // fall back to the slug useStartDiagnostic stashed (signup opened from
  // /diagnostic). PEEK, never consume — completeAuth still read-and-clears it
  // to tailor the placement run. Each test funnel that ships a playbook (see
  // lib/lead-magnets-data — SAT, ACT, SC READY, CAASPP, NC EOG/EOC) earns it.
  const leadMagnet = React.useMemo(
    () => leadMagnetForSlug(worryTest?.slug ?? peekDiagnosticTestSlug()),
    [worryTest],
  );

  // A verified code lands us on the post-verify panels (the user now exists +
  // is signed in) rather than finishing outright: "worry" (test-landing
  // signups only — asked FIRST so the answer is captured even if they bail
  // before finishing the profile), then "about" (role + tests), then
  // "profile" (name + email), where completeAuth runs after saving.
  const otp = useOtpVerification(
    normalizedPhone,
    async (leadEventId) => {
      setErr(null);
      if (!leadFiredRef.current) {
        // Meta Pixel lead: the visitor verified their phone via OTP at signup.
        // A confirmed, contactable number is the real lead — fire it here even
        // though name/email come later (they may bail on the profile step).
        // No-ops off-prod / when the pixel id is unset; Meta attributes
        // ad-referred ones via its own cookies. Sign-in never reaches this
        // closure (it uses completeAuth), so a Lead only fires for signups.
        // leadEventId matches the server-side Conversions API Lead → dedup.
        // `exposure` carries the test-landing experiment arm (TEX-1637) when
        // this browser saw one — onto both the pixel params and the PostHog
        // `lead_captured` event (the experiment's primary metric).
        const exposure = getExposure();
        trackMetaEvent(
          "Lead",
          leadEventParams(
            { content_name: "phone_signup", source: "popup" },
            getAttribution(),
            exposure,
          ),
          leadEventId,
        );
        track.leadCaptured({ source: "popup", ...(exposure ?? {}) });
        leadFiredRef.current = true;
      }
      // Lead magnet: a confirmed number on a magnet funnel (e.g. /sat) earns the
      // free guide right now — auto-download + a congrats toast — even though the
      // about/profile steps still follow (the visitor may bail on those). Fire
      // once. The toast carries a manual download button as the fallback for
      // when the browser blocks the programmatic download.
      if (leadMagnet && !playbookFiredRef.current) {
        playbookFiredRef.current = true;
        const magnetHref = leadMagnetHref(leadMagnet);
        triggerDownload(magnetHref, leadMagnet.downloadName);
        toast?.show({
          sticker: "Free",
          eyebrow: "Congrats",
          title: leadMagnet.toastTitle,
          body: leadMagnet.toastBody,
          action: {
            label: leadMagnet.toastAction,
            href: magnetHref,
            download: leadMagnet.downloadName,
          },
          duration: 10000,
        });
      }
      setStep(worryTest ? "worry" : "about");
    },
    marketing,
  );

  React.useEffect(() => {
    const ref =
      step === "phone"
        ? phoneRef
        : step === "worry"
          ? worryRef
          : step === "profile"
            ? firstRef
            : null;
    if (!ref) return;
    const t = window.setTimeout(() => ref.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [step]);

  const phoneValid = normalizePhone(phone) !== null;

  async function doSend(normalized: string) {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      // intent:"signup" lets the server reject an already-registered number
      // before any SMS goes out — see the 409 branch below. (Sign-in omits it
      // so existing users still get a code.)
      await apiFetch("/auth/phone-otp", {
        method: "POST",
        body: { phone: normalized, intent: "signup" },
      });
      setNormalizedPhone(normalized);
      otp.armForCode();
      setStep("otp");
    } catch (e2) {
      if (e2 instanceof ApiError && e2.status === 409) {
        setErr("That number already has an account — use “Sign in” below.");
      } else {
        setErr(authErrorMessage(e2, "send"));
      }
    } finally {
      setBusy(false);
    }
  }

  // Page-1 submit. If they haven't opted in (and we haven't already asked),
  // surface the one-time nudge before sending the code. Opting in is never a
  // condition of proceeding — both nudge choices continue the flow.
  function onPhoneSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (busy) return;
    const normalized = normalizePhone(phone);
    if (!normalized) {
      setErr("Please enter a valid mobile number.");
      return;
    }
    if (!marketing && !nudgeResolved) {
      setShowNudge(true);
      return;
    }
    void doSend(normalized);
  }

  function nudgeOptIn() {
    setMarketing(true);
    setNudgeResolved(true);
    setShowNudge(false);
    const normalized = normalizePhone(phone);
    if (normalized) void doSend(normalized);
  }

  function nudgeDecline() {
    setNudgeResolved(true);
    setShowNudge(false);
    const normalized = normalizePhone(phone);
    if (normalized) void doSend(normalized);
  }

  // Persist the worry answer the moment it's submitted (its own endpoint, not
  // a complete-profile field) so a later bail-out can't lose it. Required is
  // enforced in the UI (submit disabled while empty); a server hiccup shows
  // the same inline error + retry the profile step uses.
  async function saveWorry(e?: React.FormEvent) {
    e?.preventDefault();
    if (busy || !worryTest) return;
    const trimmed = worry.trim();
    if (!trimmed) return;
    setBusy(true);
    setErr(null);
    try {
      await apiFetch("/auth/test-worry", {
        method: "POST",
        body: { test_slug: worryTest.slug, worry: trimmed },
      });
      track.testWorrySubmitted({ test_slug: worryTest.slug, variant: worryTest.variant });
      setStep("about");
    } catch (e2) {
      setErr(authErrorMessage(e2, "signup"));
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile(e?: React.FormEvent) {
    e?.preventDefault();
    if (busy) return;
    if (!first.trim() || !last.trim()) {
      setErr("Please enter your first name and last name.");
      return;
    }
    if (!isValidName(first) || !isValidName(last)) {
      setErr(NAME_HELP);
      return;
    }
    const trimmedEmail = email.trim();
    if (trimmedEmail && !/\S+@\S+\.\S+/.test(trimmedEmail)) {
      setErr("Please enter a valid email, or leave it blank.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {
        role,
        first_name: first.trim(),
        last_name: last.trim(),
        marketing_opt_in: marketing,
      };
      if (trimmedEmail) body.email = trimmedEmail;
      if (studyingFor.length > 0) body.studying_for = studyingFor;
      if (homeState) body.state = homeState;
      await apiFetch("/auth/complete-profile", { method: "POST", body });
      track.signupCompleted({ role });
      // "Challenge your kid" (TEX-1765): parents on the placement funnel get one
      // more step instead of dropping straight into the test.
      if (offerChallenge && role === "parent" && onStartChallenge) {
        setStep("challenge");
        return;
      }
      await onComplete();
    } catch (e2) {
      if (e2 instanceof ApiError && e2.status === 409) {
        setErr("That email already has an account — try another, or leave it blank.");
      } else {
        setErr(authErrorMessage(e2, "signup"));
      }
    } finally {
      setBusy(false);
    }
  }

  // "Challenge your kid" (TEX-1765): create the challenge, then hand off to the
  // lobby. The kid opens the invite link from there. The test slug the parent
  // came from tailors BOTH runs (it survives because this path skips the normal
  // completeAuth start).
  async function createChallenge(participates: boolean) {
    if (busy || !onStartChallenge) return;
    setBusy(true);
    setErr(null);
    try {
      const stashed = readAndClearDiagnosticTestSlug();
      const pathSlug = window.location.pathname.split("/").filter(Boolean);
      const derived =
        pathSlug.length === 1 && pathSlug[0] && TEST_LANDING_BY_SLUG[pathSlug[0]]
          ? pathSlug[0]
          : null;
      const testSlug = stashed ?? derived;
      const res = await apiFetch<{ code: string }>("/v1/placement-challenges", {
        method: "POST",
        body: {
          parent_participates: participates,
          ...(childName.trim() ? { child_first_name: childName.trim() } : {}),
          ...(testSlug ? { test_slug: testSlug } : {}),
        },
      });
      onStartChallenge(res.code);
    } catch (e2) {
      setErr(authErrorMessage(e2, "signup"));
    } finally {
      setBusy(false);
    }
  }

  let body: React.ReactElement;
  if (step === "otp") {
    body = (
      <OtpStep
        titleId={titleId}
        eyebrow="Code sent"
        cta="Verify & continue"
        phoneDisplay={normalizedPhone ? formatPhone(normalizedPhone) : phone}
        ctx={otp}
        onBack={() => {
          setStep("phone");
          setErr(null);
          otp.reset();
        }}
      />
    );
  } else if (step === "worry" && worryTest) {
    // The dedicated post-Lead question (TEX-1639) — one open-text answer,
    // nothing else on this view. The placeholder stays generic on purpose:
    // a leading example would prime the "authentic parent language" we're
    // mining for ad copy.
    body = (
      <>
        <span className="auth-gate__sticker">Quick question</span>
        <div className="auth-gate__head">
          <span className="auth-gate__eye">You&rsquo;re in</span>
          <h2 id={titleId} className="auth-gate__title">
            What worries you most about the{" "}
            <span className="auth-gate__highlight">{worryTest.shortName}</span>?
          </h2>
          <p className="auth-gate__body">Tell us in your own words — we read every answer.</p>
        </div>
        <form className="auth-gate__form auth-gate__form--final" onSubmit={saveWorry} noValidate>
          <div className="auth-gate__field">
            <label className="auth-gate__label" htmlFor="auth-gate-worry">
              Your answer <span className="auth-gate__req">*</span>
            </label>
            <textarea
              ref={worryRef}
              id="auth-gate-worry"
              className="auth-gate__input auth-gate__textarea"
              rows={4}
              maxLength={2000}
              placeholder="Type your answer…"
              value={worry}
              onChange={(e) => setWorry(e.target.value)}
            />
          </div>
          {err && (
            <div className="auth-gate__err" role="alert">
              {err}
            </div>
          )}
          <button
            type="submit"
            className="auth-gate__submit"
            disabled={worry.trim().length === 0 || busy}
          >
            {busy ? "Saving…" : "Continue"}
            <span className="auth-gate__arc" aria-hidden="true">
              <ArrowIcon />
            </span>
          </button>
        </form>
      </>
    );
  } else if (step === "about") {
    body = (
      <>
        <span className="auth-gate__sticker">About you</span>
        <div className="auth-gate__head">
          <span className="auth-gate__eye">You&rsquo;re verified</span>
          <h2 id={titleId} className="auth-gate__title">
            Tell us about <span className="auth-gate__highlight">yourself</span>.
          </h2>
          <p className="auth-gate__body">This helps us point you in the right direction.</p>
        </div>
        <form
          className="auth-gate__form auth-gate__form--final"
          onSubmit={(e) => {
            e.preventDefault();
            setErr(null);
            setStep("profile");
          }}
          noValidate
        >
          <div className="auth-gate__field">
            <label className="auth-gate__label">I am a&hellip;</label>
            <div className="auth-gate__roles" role="radiogroup" aria-label="Account type">
              <button
                type="button"
                role="radio"
                aria-checked={role === "parent"}
                className={`auth-gate__role ${role === "parent" ? "is-active" : ""}`}
                onClick={() => setRole("parent")}
              >
                <span className="auth-gate__role-icon" aria-hidden="true">
                  <ParentIcon />
                </span>
                Parent
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={role === "student"}
                className={`auth-gate__role ${role === "student" ? "is-active" : ""}`}
                onClick={() => setRole("student")}
              >
                <span className="auth-gate__role-icon" aria-hidden="true">
                  <StudentIcon />
                </span>
                Student
              </button>
            </div>
          </div>
          <div className="auth-gate__field">
            <label className="auth-gate__label" htmlFor="auth-gate-studying">
              {role === "parent"
                ? "My child is studying for the\u2026"
                : "I am studying for the\u2026"}
            </label>
            <MultiSelectCombobox
              id="auth-gate-studying"
              options={STUDYING_FOR_OPTIONS}
              value={studyingFor}
              onChange={setStudyingFor}
              placeholder="Search or type a test…"
              ariaLabel="Standardized tests you're studying for"
              sheetTitle="Which tests?"
              allowCustom
            />
          </div>
          <button type="submit" className="auth-gate__submit">
            Continue
            <span className="auth-gate__arc" aria-hidden="true">
              <ArrowIcon />
            </span>
          </button>
        </form>
      </>
    );
  } else if (step === "profile") {
    body = (
      <>
        <span className="auth-gate__sticker">Last step</span>
        <div className="auth-gate__head">
          <BackButton
            onClick={() => {
              setStep("about");
              setErr(null);
            }}
          />
          <span className="auth-gate__eye">Almost done</span>
          <h2 id={titleId} className="auth-gate__title">
            What should we <span className="auth-gate__highlight">call you</span>?
          </h2>
          <p className="auth-gate__body">Add your name to finish.</p>
        </div>
        <form className="auth-gate__form auth-gate__form--final" onSubmit={saveProfile} noValidate>
          <div className="auth-gate__row2">
            <div className="auth-gate__field">
              <label className="auth-gate__label" htmlFor="auth-gate-first">
                First name <span className="auth-gate__req">*</span>
              </label>
              <input
                ref={firstRef}
                id="auth-gate-first"
                className="auth-gate__input"
                type="text"
                autoComplete="given-name"
                placeholder="Alex"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                maxLength={80}
              />
            </div>
            <div className="auth-gate__field">
              <label className="auth-gate__label" htmlFor="auth-gate-sur">
                Last name <span className="auth-gate__req">*</span>
              </label>
              <input
                id="auth-gate-sur"
                className="auth-gate__input"
                type="text"
                autoComplete="family-name"
                placeholder="Rivera"
                value={last}
                onChange={(e) => setLast(e.target.value)}
                maxLength={80}
              />
            </div>
          </div>
          <div className="auth-gate__field">
            <label className="auth-gate__label" htmlFor="auth-gate-email">
              Email
            </label>
            <input
              id="auth-gate-email"
              className="auth-gate__input"
              type="email"
              autoComplete="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="auth-gate__field">
            <label className="auth-gate__label" htmlFor="auth-gate-state">
              What state are you in?
            </label>
            <select
              id="auth-gate-state"
              className="auth-gate__input"
              value={homeState}
              onChange={(e) => setHomeState(e.target.value)}
              aria-label="Your US state"
            >
              <option value="">Select your state…</option>
              {US_STATES.map((s) => (
                <option key={s.abbr} value={s.abbr}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          {err && (
            <div className="auth-gate__err" role="alert">
              {err}
            </div>
          )}
          <button type="submit" className="auth-gate__submit" disabled={busy}>
            {busy ? "Finishing…" : "Finish & continue"}
            <span className="auth-gate__arc" aria-hidden="true">
              <ArrowIcon />
            </span>
          </button>
        </form>
      </>
    );
  } else if (step === "challenge") {
    const childFirst = childName.trim();
    body = (
      <>
        <span className="auth-gate__sticker">One more thing</span>
        <div className="auth-gate__head">
          <span className="auth-gate__eye">Make it a contest</span>
          <h2 id={titleId} className="auth-gate__title">
            Challenge your <span className="auth-gate__highlight">kid</span> to beat you.
          </h2>
          <p className="auth-gate__body">
            Send {childFirst || "your child"} the same placement test and see who comes out on top.
            You&rsquo;ll both get the exact same questions, and a side-by-side scorecard at the end.
          </p>
        </div>
        <form
          className="auth-gate__form auth-gate__form--final"
          onSubmit={(e) => {
            e.preventDefault();
            void createChallenge(true);
          }}
          noValidate
        >
          <div className="auth-gate__field">
            <label className="auth-gate__label" htmlFor="auth-gate-child">
              Your kid&rsquo;s first name
            </label>
            <input
              id="auth-gate-child"
              className="auth-gate__input"
              type="text"
              autoComplete="off"
              placeholder="Sam"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
            />
          </div>
          <button type="submit" className="auth-gate__submit" disabled={busy}>
            {busy ? "Setting up…" : "Challenge them — we'll both play"}
            <span className="auth-gate__arc" aria-hidden="true">
              <ArrowIcon />
            </span>
          </button>
          <button
            type="button"
            className="auth-gate__cta auth-gate__cta--secondary"
            disabled={busy}
            onClick={() => void createChallenge(false)}
          >
            Just send it to {childFirst || "my kid"} &mdash; I&rsquo;ll sit this one out
          </button>
          <button
            type="button"
            className="auth-gate__link"
            disabled={busy}
            onClick={() => void onComplete()}
          >
            Skip &mdash; I&rsquo;ll just take it myself
          </button>
          {err && (
            <div className="auth-gate__err" role="alert">
              {err}
            </div>
          )}
        </form>
      </>
    );
  } else {
    body = (
      <>
        <span className="auth-gate__sticker">Free account</span>
        <div className="auth-gate__head">
          <span className="auth-gate__eye">Join free</span>
          <h2 id={titleId} className="auth-gate__title">
            Create your <span className="auth-gate__highlight">account</span>.
          </h2>
          <p className="auth-gate__body">
            Just your number to start — <strong>free forever</strong>, no card needed.
          </p>
          {leadMagnet && (
            <p className="auth-gate__bonus">
              <span className="auth-gate__bonus-tag">Free gift</span>
              <span>
                Sign up to get our <strong>{leadMagnet.title}</strong> now.
              </span>
            </p>
          )}
        </div>
        <form className="auth-gate__form" onSubmit={onPhoneSubmit} noValidate>
          <div className="auth-gate__field">
            <label className="auth-gate__label" htmlFor="auth-gate-suphone">
              Mobile number <span className="auth-gate__req">*</span>
            </label>
            <input
              ref={phoneRef}
              id="auth-gate-suphone"
              className="auth-gate__input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(555) 123-4567"
              value={phone}
              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
            />
            <p className="auth-gate__hint">
              We&rsquo;ll text a 6-digit code to verify your number. Msg &amp; data rates may apply.
            </p>
          </div>
          <MarketingConsent variant="auth-gate" checked={marketing} onChange={setMarketing} />
          {err && (
            <div className="auth-gate__err" role="alert">
              {err}
            </div>
          )}
          <button type="submit" className="auth-gate__submit" disabled={!phoneValid || busy}>
            {busy ? "Sending…" : "Text me a code"}
            <span className="auth-gate__arc" aria-hidden="true">
              <ArrowIcon />
            </span>
          </button>
        </form>
        <div className="auth-gate__foot">
          <span>
            Already a member?{" "}
            <button type="button" className="auth-gate__link" onClick={onSwitchToSignIn}>
              Sign in
            </button>
          </span>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="auth-gate__step" key={step}>
        {body}
      </div>
      {/* Rendered at the modal level (a sibling of the animated step) so the
       * overlay covers the whole card — the step carries a transform, which
       * would otherwise clip an absolutely-positioned child to the step box. */}
      {showNudge && (
        <OptInNudge
          onOptIn={nudgeOptIn}
          onDecline={nudgeDecline}
          onCancel={() => setShowNudge(false)}
        />
      )}
    </>
  );
}

// ─── Shared OTP step + verification hook ─────────────────────────────

/** Seconds before a sent code can be resent. */
const RESEND_SECONDS = 30;

interface OtpController {
  otp: string;
  setOtp: (v: string) => void;
  otpError: boolean;
  busy: boolean;
  err: string | null;
  notice: string | null;
  resendIn: number;
  verify: (full: string) => void;
  resend: () => void;
  /** Clear everything incl. the cooldown — call when LEAVING the OTP step. */
  reset: () => void;
  /** Clear + (re)start the cooldown — call on arrival after a fresh send. */
  armForCode: () => void;
}

/** Owns the OTP-step state shared by sign-in and sign-up: code entry,
 * `/auth/phone-verify`, resend (with a 30s cooldown), and error/notice
 * copy. `normalizedPhone` is whatever the prior step captured; on a
 * verified code it calls `onComplete` (which navigates + closes) with the
 * Lead dedup event id sent to the server, so a signup flow can fire the
 * matching browser pixel event (sign-in callbacks just ignore it). */
function useOtpVerification(
  normalizedPhone: string | null,
  onComplete: (leadEventId: string) => Promise<void>,
  // Signup popup only: the SMS marketing consent gathered on the first screen.
  // Persisted with the verify call so it survives an abandoned profile step.
  marketingOptIn?: boolean,
): OtpController {
  const [otp, setOtp] = React.useState("");
  const [otpError, setOtpError] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [resendIn, setResendIn] = React.useState(0);

  React.useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setTimeout(() => setResendIn((r) => r - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);

  // Full reset, used when LEAVING the OTP step (Back) so a later re-entry
  // doesn't carry a stale resend countdown.
  const reset = React.useCallback(() => {
    setOtp("");
    setOtpError(false);
    setErr(null);
    setNotice(null);
    setResendIn(0);
  }, []);

  // Arrive at the OTP step after a fresh code send (initial send): clear the
  // boxes/errors and (re)start the cooldown so it reflects the latest send,
  // not a countdown left over from a previous visit to this step.
  const armForCode = React.useCallback(() => {
    setOtp("");
    setOtpError(false);
    setErr(null);
    setNotice(null);
    setResendIn(RESEND_SECONDS);
  }, []);

  const verify = React.useCallback(
    async (full: string) => {
      if (!normalizedPhone || busy) return;
      if (full.length !== 6) {
        // Manual "Verify" tap with an empty/short code: surface the inline
        // error and shake the boxes instead of silently doing nothing.
        setOtpError(true);
        setErr("Enter the 6-digit code we texted you.");
        return;
      }
      setBusy(true);
      setErr(null);
      setNotice(null);
      setOtpError(false);
      try {
        const { body, leadEventId } = buildPhoneVerifyBody(normalizedPhone, full, marketingOptIn);
        await apiFetch("/auth/phone-verify", { method: "POST", body });
        await onComplete(leadEventId);
      } catch (e2) {
        setOtpError(true);
        setOtp("");
        setErr(authErrorMessage(e2, "verify"));
      } finally {
        setBusy(false);
      }
    },
    [normalizedPhone, busy, onComplete, marketingOptIn],
  );

  const resend = React.useCallback(async () => {
    if (!normalizedPhone || busy || resendIn > 0) return;
    setBusy(true);
    setErr(null);
    setNotice(null);
    setOtp("");
    try {
      await apiFetch("/auth/phone-otp", {
        method: "POST",
        body: { phone: normalizedPhone },
      });
      setNotice("A fresh code is on its way.");
      setResendIn(RESEND_SECONDS);
    } catch (e2) {
      setErr(authErrorMessage(e2, "send"));
    } finally {
      setBusy(false);
    }
  }, [normalizedPhone, busy, resendIn]);

  return {
    otp,
    setOtp,
    otpError,
    busy,
    err,
    notice,
    resendIn,
    verify,
    resend,
    reset,
    armForCode,
  };
}

interface OtpStepProps {
  titleId: string;
  eyebrow: string;
  cta: string;
  phoneDisplay: string;
  ctx: OtpController;
  onBack: () => void;
}

function OtpStep({
  titleId,
  eyebrow,
  cta,
  phoneDisplay,
  ctx,
  onBack,
}: OtpStepProps): React.ReactElement {
  return (
    <>
      <span className="auth-gate__sticker">Verify</span>
      <div className="auth-gate__head">
        <BackButton onClick={onBack} />
        <span className="auth-gate__eye">{eyebrow}</span>
        <h2 id={titleId} className="auth-gate__title">
          Enter your <span className="auth-gate__highlight">code</span>.
        </h2>
        <p className="auth-gate__body">
          We texted a 6-digit code to <strong>{phoneDisplay}</strong>. Enter it below to continue.
        </p>
      </div>
      <div className="auth-gate__form">
        <div className="auth-gate__field">
          <label className="auth-gate__label">
            One-time code <span className="auth-gate__req">*</span>
          </label>
          <OtpGrid
            baseClass="auth-gate__otp"
            value={ctx.otp}
            onChange={ctx.setOtp}
            onComplete={ctx.verify}
            error={ctx.otpError}
          />
        </div>
        {ctx.err && (
          <div className="auth-gate__err" role="alert">
            {ctx.err}
          </div>
        )}
        {ctx.notice && (
          <div className="auth-gate__notice" role="status">
            {ctx.notice}
          </div>
        )}
        <button
          type="button"
          className="auth-gate__submit"
          onClick={() => ctx.verify(ctx.otp)}
          disabled={ctx.busy}
        >
          {cta}
          <span className="auth-gate__arc" aria-hidden="true">
            <ArrowIcon />
          </span>
        </button>
      </div>
      <div className="auth-gate__foot">
        <button
          type="button"
          className="auth-gate__link"
          onClick={ctx.resend}
          disabled={ctx.busy || ctx.resendIn > 0}
        >
          {ctx.resendIn > 0 ? `Resend in ${ctx.resendIn}s` : "Resend code"}
        </button>
        {/* Only once resending is the remaining option — before that, waiting
            for the text is the answer and a support link would just be noise. */}
        {(ctx.err || ctx.resendIn === 0) && <SupportHint lead="Code never arrived?" />}
      </div>
    </>
  );
}

/** Maps an auth API failure to a short, user-facing message. */
function authErrorMessage(e: unknown, ctx: "send" | "verify" | "signup"): string {
  if (e instanceof ApiError) {
    if (e.status === 429) return "Too many requests — try again in a few minutes.";
    if (e.status === 400) {
      if (ctx === "verify") return "Invalid or expired code. Try again.";
      if (ctx === "send")
        return "Invalid phone number. Use a US number or include the country code.";
      return "Please check your details and try again.";
    }
    if (e.status === 502) return "Couldn't send the code. Please try again.";
    return ctx === "verify"
      ? `Verification failed (${e.status}).`
      : `Something went wrong (${e.status}). Please try again.`;
  }
  return "Network error. Please try again.";
}

/**
 * One-time, on-submit confirmation that nudges an opt-out parent to opt in to
 * marketing SMS. Compliance guardrails: both choices proceed (consent is never
 * a condition), "No thanks" is an equally-prominent button, and Escape/backdrop
 * simply cancels back to the form — no pre-selection, no trap.
 */
function OptInNudge({
  onOptIn,
  onDecline,
  onCancel,
}: {
  onOptIn: () => void;
  onDecline: () => void;
  onCancel: () => void;
}): React.ReactElement {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const primaryRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    primaryRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      // Trap Tab within the dialog (ARIA modal-dialog pattern) so focus can't
      // land on the phone field or the main modal's close button behind the
      // overlay. Skip the collapsed-consent links (they're not visible).
      if (e.key !== "Tab") return;
      const root = cardRef.current;
      if (!root) return;
      const focusable = Array.from(
        root.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"),
      ).filter((el) => !el.closest(".tcpa-consent__full:not(.is-open)"));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !root.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="auth-gate__nudge"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-gate-nudge-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="auth-gate__nudge-card" ref={cardRef}>
        <h3 id="auth-gate-nudge-title" className="auth-gate__nudge-title">
          Opt in to SMS
        </h3>
        <p className="auth-gate__nudge-body">
          We won&rsquo;t be able to text you unless you agree to be contacted.
        </p>
        {/* The disclosure sits with the affirmative action so "Opt me in" is a
         * valid, self-contained TCPA consent. */}
        <ConsentDisclosure textClassName="auth-gate__nudge-consent" />
        <div className="auth-gate__nudge-actions">
          <button ref={primaryRef} type="button" className="auth-gate__submit" onClick={onOptIn}>
            Opt me in
            <span className="auth-gate__arc" aria-hidden="true">
              <ArrowIcon />
            </span>
          </button>
          <button type="button" className="auth-gate__nudge-decline" onClick={onDecline}>
            No thanks, continue
          </button>
        </div>
      </div>
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }): React.ReactElement {
  return (
    <button type="button" className="auth-gate__back" onClick={onClick}>
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
      Back
    </button>
  );
}

// ─── Paywall modal ───────────────────────────────────────────────────

interface PaywallModalProps {
  onClose: (reason: DismissReason) => void;
  /** The play surface the visitor was headed to — handed to checkout so the
   *  success landing can return them to it once Premium activates. */
  intendedHref: string | null;
}

function PaywallModal({ onClose, intendedHref }: PaywallModalProps): React.ReactElement | null {
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const { primaryRef } = useModalChrome(onClose);
  const { subscribe, busy, error, emailModal } = useSubscribe({ next: intendedHref });
  const [showCoupon, setShowCoupon] = React.useState(false);
  const [coupon, setCoupon] = React.useState("");

  function onOverlayMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) onClose("overlay");
  }

  const titleId = "auth-gate-paywall-title";
  const node = (
    <div
      ref={overlayRef}
      className="auth-gate__overlay auth-gate--paywall"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-source="premium-required"
      onMouseDown={onOverlayMouseDown}
    >
      <div className="auth-gate__modal">
        <span className="auth-gate__sticker">★ Premium only</span>
        <CloseButton onClick={() => onClose("x")} />
        <div className="auth-gate__head">
          <span className="auth-gate__eye">Free account · games locked</span>
          <h2 id={titleId} className="auth-gate__title">
            Premium unlocks <span className="auth-gate__highlight">all the games</span>.
          </h2>
          <p className="auth-gate__body">
            Subscribe to Premium and the entire game catalog opens up — including the ones our
            adaptive engine matched to your placement.
          </p>
        </div>
        <div className="auth-gate__price">
          <span className="auth-gate__price-currency">$</span>
          <span className="auth-gate__price-amount">200</span>
          <span className="auth-gate__price-per">/month</span>
        </div>
        <div className="auth-gate__coupon">
          {showCoupon ? (
            <input
              type="text"
              className="auth-gate__coupon-input"
              placeholder="Coupon code"
              aria-label="Coupon code"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          ) : (
            <button
              type="button"
              className="auth-gate__coupon-toggle"
              onClick={() => setShowCoupon(true)}
            >
              Have a coupon code?
            </button>
          )}
        </div>
        <div className="auth-gate__ctas">
          <button
            ref={primaryRef}
            type="button"
            className="auth-gate__cta auth-gate__cta--primary"
            disabled={busy}
            onClick={() => {
              track.upsellCtaClicked({
                kind: "paywall",
                source: "premium",
                cta: "subscribe",
              });
              subscribe(coupon);
            }}
          >
            {busy ? "Redirecting…" : "Subscribe"}
            <span className="auth-gate__arc" aria-hidden="true">
              <ArrowIcon />
            </span>
          </button>
          <Link
            to={intendedHref ? `/plans?next=${encodeURIComponent(intendedHref)}` : "/plans"}
            className="auth-gate__cta auth-gate__cta--secondary"
            onClick={() => {
              track.upsellCtaClicked({
                kind: "paywall",
                source: "premium",
                cta: "see_the_plan",
              });
              onClose("cta");
            }}
          >
            See the plan
            <span className="auth-gate__arc" aria-hidden="true">
              <ArrowIcon />
            </span>
          </Link>
        </div>
        {error && (
          <>
            <p role="alert" style={{ margin: "12px 30px 0", color: "#c0392b", fontSize: 13.5 }}>
              {error}
            </p>
            <SupportHint lead="Payment won't start?" className="auth-gate__support" />
          </>
        )}
        <ModalFoot onDismiss={() => onClose("maybe_later")} />
      </div>
      {emailModal}
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}

// ─── Shared bits ─────────────────────────────────────────────────────

function CloseButton({ onClick }: { onClick: () => void }): React.ReactElement {
  return (
    <button type="button" className="auth-gate__close" aria-label="Close" onClick={onClick}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <line x1="6" y1="6" x2="18" y2="18" />
        <line x1="18" y1="6" x2="6" y2="18" />
      </svg>
    </button>
  );
}

function ModalFoot({ onDismiss }: { onDismiss: () => void }): React.ReactElement {
  return (
    <div className="auth-gate__foot">
      <button type="button" className="auth-gate__dismiss" onClick={onDismiss}>
        Maybe later
      </button>
    </div>
  );
}

function ArrowIcon(): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function ParentIcon(): React.ReactElement {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="9" cy="8" r="3.5" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c0-3 2.7-5 6-5s6 2 6 5" />
      <path d="M15 20c0-2 1.3-4 3.5-4s3.5 2 3.5 4" />
    </svg>
  );
}

function StudentIcon(): React.ReactElement {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 9l10-4 10 4-10 4z" />
      <path d="M6 11v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" />
    </svg>
  );
}
